import * as React from 'react';
import { Fragment } from 'react';

import { AgencyReportUploadRepository } from '../../MedicareProAPI/Repositories/Agency/AgencyReportUploadRepository';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import type { AgencyReportUpload } from '../../MedicareProAPI/Entities/Agency/AgencyReportUpload';
import { ContractManagementImport } from '../Components/ContractManagementImport';
import { ContractCreationImport } from '../Components/ContractCreationImport';

export interface AgencySettingUploadPanelProps {
  userAccessToken: string;
  userId: number;
  agencyId: number;
}

/**
 * Panel for managing AgencyReportUpload
 *
 * @author Andrew Brown <abrown@medicareprocrm.com>
 */
export const AgencySettingsUploadsPanel = (props: AgencySettingUploadPanelProps) => {
  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  const agencyReportUploadRepository = new AgencyReportUploadRepository();
  agencyReportUploadRepository.setAccessToken(props.userAccessToken);

  const [agencyReportUploads, setAgencyReportUploads] = React.useState<Array<AgencyReportUpload>>(
    []
  );
  const [agencyReportUpload, setAgencyReportUpload] = React.useState<AgencyReportUpload>(null);
  const [uploadTypeSelected, setUploadTypeSelected] = React.useState<number>();

  const uploadButtons = [
    { name: 'Contract Creation', label: 'Contract Creation' },
    { name: 'Contract Management', label: 'Contract Management' },
  ];
  const uploadButtonClass = ['btn', 'btn-sm', 'mr-2', 'cp', 'border-primary'];

  /**
   * Handles click on upload type button
   */
  const handleGetFilesForUploadType = (itemIndex: number) => {
    setAgencyReportUpload(null);
    setAgencyReportUploads(null);
    setUploadTypeSelected(itemIndex);
    agencyReportUploadRepository
      .getAgencyReportUploads(
        props.userId,
        props.agencyId,
        { include: ['company'] },
        { uploadType: uploadButtons[itemIndex].name }
      )
      .then((response) => {
        if (response.getItems().length > 0) {
          setAgencyReportUploads(response.getItems());
          //setAgencyReportUpload(response.getItem(0));
        }
      });
  };

  /**
   * Handles clicking a recently uploaded AgencyReportUpload
   */
  const handleAgencyReportUploadClicked = (itemIndex: number) => {
    if (agencyReportUploads) {
      setAgencyReportUpload(agencyReportUploads[itemIndex]);
    }
  };

  /**
   * Handles setting a newly created AgencyReportUpload into the collection
   */
  const handleFileCreate = (newUpload: AgencyReportUpload) => {
    setAgencyReportUploads((prevUploads = []) => [newUpload, ...prevUploads]);
    setAgencyReportUpload(newUpload);
  };

  /**
   * Handles clicking "Go Back" - resets AgencyReportUpload
   */
  const handleGoBack = () => {
    setAgencyReportUpload(undefined);
  };

  /**
   * Handles downloading a report template
   */
  const handleTemplateDownload = (templateName: string) => {
    const fileName = `${templateName}_import_template.xlsx`;
    window.open(
      `https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/templates/${fileName}`,
      '_blank'
    );
  };

  /**
   * Handles updating a processed AgencyReportUpload into the collection
   */
  const handleProcessedFile = (updatedUpload: AgencyReportUpload) => {
    setAgencyReportUploads((prevUploads = []) => {
      const filteredReports = prevUploads.filter((item) => item.getId() !== updatedUpload.getId());
      return [updatedUpload, ...filteredReports];
    });

    setAgencyReportUpload(updatedUpload);
  };

  const handleUpdateAgencyReportUpload = (reportId: number, payload: object = {}) => {
    agencyReportUploadRepository
      .updateAgencyReportUpload(props.userId, props.agencyId, reportId, payload)
      .then((response) => {
        setAgencyReportUploads((prevUploads = []) => {
          const filteredReports = prevUploads.filter((item) => item.getId() !== response.getId());
          return [response, ...filteredReports];
        });
        setAgencyReportUpload(response);
      });
  };

  return (
    <div className="card cardAddress">
      <div className="card-header">Uploads</div>
      <div className="card-body">
        <div className="row gutter-2 mb-2 p-2">
          <div className="col-12">
            {uploadButtons.map((item, itemIndex) => {
              const isSelected = uploadTypeSelected === itemIndex;
              const buttonClasses = [...uploadButtonClass, isSelected ? 'btn-primary' : ''].join(
                ' '
              );

              return (
                <div
                  key={itemIndex}
                  className={buttonClasses}
                  onClick={() => handleGetFilesForUploadType(itemIndex)}
                >
                  <i className="fal fa-plus-circle mr-2"></i>
                  {item.label}
                </div>
              );
            })}
          </div>
        </div>

        {uploadTypeSelected !== undefined && (
          <div className="row gutter-2 mb-2 p-2">
            {/* Left Panel: Recent Uploads */}
            <div className="col-4 border-right">
              <b>Recent {uploadButtons[uploadTypeSelected].name} uploads</b>
              {agencyReportUploads &&
                agencyReportUploads.map((item, itemIndex) => {
                  const isSelected = agencyReportUpload?.getId() === item.getId();
                  const fileClass = [
                    'fileInstance',
                    'col-12',
                    'pt-2',
                    'pb-1',
                    'mt-2',
                    'rounded',
                    'font-weight-bold',
                    'calMess',
                    isSelected ? 'bg-light' : '',
                  ].join(' ');

                  return (
                    <div
                      key={item.getId()}
                      className={fileClass}
                      onClick={() => handleAgencyReportUploadClicked(itemIndex)}
                      style={{ border: '2px dotted #999999' }}
                    >
                      <span>
                        <i
                          className="far fa-file-import text-success mr-2"
                          style={{ fontSize: '16pt' }}
                        ></i>
                      </span>
                      {item.getFileName()}
                      <div>
                        <span className="badge badge-danger">{item.getPlanYear()}</span>
                        <span className="badge badge-info ml-2">
                          {item.getCompany().getCompanyName()}
                        </span>
                        <span className="badge badge-success ml-2">{item.getStatus() || ''}</span>
                      </div>
                      <div className="font-weight-normal" style={{ fontSize: '9pt' }}>
                        Uploaded: {item.getDateTimeCreated()}
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Right Panel: Upload New File */}
            <div className="col-8">
              <b>Upload new {uploadButtons[uploadTypeSelected].name} report</b>
              <span
                className="badge badge-pill badge-info cp float-right"
                onClick={() => handleTemplateDownload(uploadButtons[uploadTypeSelected].name)}
              >
                Download sample {uploadButtons[uploadTypeSelected].name} template
              </span>

              {uploadButtons[uploadTypeSelected].name === 'Contract Management' && (
                <ContractManagementImport
                  props={props}
                  agencyReportUpload={agencyReportUpload}
                  onFileCreate={handleFileCreate}
                  handleGoBack={handleGoBack}
                  handleProcessedFile={handleProcessedFile}
                  handleOnFileUpdate={handleUpdateAgencyReportUpload}
                />
              )}

              {uploadButtons[uploadTypeSelected].name === 'Contract Creation' && (
                <ContractCreationImport
                  props={props}
                  agencyReportUpload={agencyReportUpload}
                  onFileCreate={handleFileCreate}
                  handleGoBack={handleGoBack}
                  handleProcessedFile={handleProcessedFile}
                  handleOnFileUpdate={handleUpdateAgencyReportUpload}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
